import { Client, OneDriveLargeFileUploadOptions, OneDriveLargeFileUploadTask, Range, UploadEventHandlers, UploadResult } from '@microsoft/microsoft-graph-client';
import {IAuthenticationProvider} from '../interfaces/IAuthenticationProvider'
import * as GraphClientInitOptions from '../ms-graph/GraphClientInitOptions'
import { IOneDriveItem } from '../interfaces/IOneDriveItem'
import { LogEvents } from '../../utils/LogEvents';
import { ErrorStringFromException, isNullOrUndefined, PromiseWithRetry } from '../../utils/utils';
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { OneDriveUploadTaskError, OneDriveUploadTaskErrors } from '../oneDrive/OneDriveUploadTaskErrors';
import { FRONTDOOR_UPLOAD_WAIT_TIMEOUT_MILLISECONDS, GRAPH_API_ONE_DRIVE_ITEM_FETCH_RETRY_ATTEMPTS, GRAPH_API_ONE_DRIVE_ITEM_FETCH_WAIT_TIME, RESUME_UPLOAD_RETRY_COUNT, RESUME_UPLOAD_WAIT_TIME_MILLISECONDS } from '../../AppConstants';
import { AttemptsWithTimeoutCall } from '../utils/Utils';
import { TranscriptionHistoryDump } from '../utils/TranscriptionHistoryDump';
import { GraphGetOneDriveItemByIdRequest } from '../ms-graph/GraphApiCalls';

export enum OneDriveUploadTaskResultStatus {
    Completed,
    Canceled
}

export interface IOneDriveUploadTaskResult {
    status: OneDriveUploadTaskResultStatus,
    oneDriveItem?: IOneDriveItem
}

// Used to keep track of the upload state inside of OneDriveUploadTask.
enum OneDriveUploadTaskState {
    Default, // Default state. Ready to upload.
    Uploading, // Upload is in progress.
    Canceled // Upload was canceled.
}

export class OneDriveUploadTask {
    private uploadTaskState: OneDriveUploadTaskState = OneDriveUploadTaskState.Default;
    private authProvider: IAuthenticationProvider;
    private graphUploadTask: OneDriveLargeFileUploadTask<Blob | ArrayBuffer | Uint8Array> | null = null;
    private folderName: string;
    private file: File;
    private oneDriveHistory: TranscriptionHistoryDump | null = null;
    private graphClient: Client | null;

    constructor(authProvider: IAuthenticationProvider, folderName: string, file: File, transcriptionHistoryDump: TranscriptionHistoryDump | null = null) {
        this.authProvider = authProvider;
        this.folderName = folderName;
        this.file = file;
        this.oneDriveHistory = transcriptionHistoryDump;
        this.graphClient = null;
    }

    // Need this method because TypeScript doesn't realize that this.uploadState could have changed after an await statement.
    private GetUploadState() {
        return this.uploadTaskState;
    }

    private async CreateGraphUploadTask(fileUploadProgressCallback: Function | null = null) {

        if (!isNullOrUndefined(this.oneDriveHistory)) {
          this.oneDriveHistory?.AddEventToTranscriptionHistory("File size is " + (this.file.size/1000000) + " MB.");
        }

        this.graphClient = Client.initWithMiddleware({
            baseUrl: GraphClientInitOptions.GetAuthOptions(this.authProvider).baseUrl,
            middleware: GraphClientInitOptions.GetDefaultMiddlewareChain(this.authProvider, true /*enableTelemetryHandler*/, LogEventNames.ServiceGraphClientOneDriveUpload),
            customHosts: GraphClientInitOptions.GetCustomHosts(this.authProvider)
        });

        const progress = (range?: Range | undefined) => {
          if (!isNullOrUndefined(range) && !isNullOrUndefined(fileUploadProgressCallback)) {
            fileUploadProgressCallback!(Math.round((range?.maxValue!/this.file.size) * 100));
          }
        };

        const uploadEventHandlers: UploadEventHandlers = {
          progress
        };

        const options: OneDriveLargeFileUploadOptions = {
            path: '/' + this.folderName,
            fileName: this.file.name,
            rangeSize: 3*1000000, /* 3 MB */
            uploadEventHandlers
        };

        return OneDriveLargeFileUploadTask.create(this.graphClient, this.file, options);
    }

    public async UploadFile(fileUploadProgressCallback: Function | null = null): Promise<IOneDriveUploadTaskResult> {
        LogEvents.ServiceGraphOneDriveUpload.Start();
        if (this.GetUploadState() != OneDriveUploadTaskState.Default) {
            const responseText: string = 'Called UploadFile on a previously used upload task.';
            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: responseText, isFinalResponse: true });
            throw new Error(responseText);
        }

        this.uploadTaskState = OneDriveUploadTaskState.Uploading;

        try {
            this.graphUploadTask = await this.CreateGraphUploadTask(fileUploadProgressCallback!);
        } catch (error) {
            if (this.GetUploadState() != OneDriveUploadTaskState.Canceled) {
                LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Creating upload task failed with exception: " + ErrorStringFromException(error), isFinalResponse: true });
                throw error;
            }
        }

        // If the upload was canceled while we were creating the graph upload task, make sure we cancel the task.
        if (this.GetUploadState() == OneDriveUploadTaskState.Canceled) {
            if (this.graphUploadTask) {
                await this.graphUploadTask.cancel();
            }

            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Canceled", isFinalResponse: true });
            return { status: OneDriveUploadTaskResultStatus.Canceled };
        }

        let response = null;

        try {
            response = await this.StartResumableFileUpload();
        } catch (error) {
            this.HandleErrorFromGraphUploadTask(error)
        }

        // Upload was canceled while uploading
        if (this.GetUploadState() == OneDriveUploadTaskState.Canceled) {
            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Canceled", isFinalResponse: true });
            return { status: OneDriveUploadTaskResultStatus.Canceled };
        }

        if (response == null) {
            const responseText: string = 'Response was null in non-canceled upload';
            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: responseText, isFinalResponse: true });
            throw new Error(responseText);
        }

        let responseBody = <any>response.responseBody;
        LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: true, message: "Upload successful", isFinalResponse: true });
        return {
            status: OneDriveUploadTaskResultStatus.Completed,
            oneDriveItem: { driveId: responseBody.parentReference.driveId, itemId: responseBody.id }
        };
    }

    private HandleErrorFromGraphUploadTask(error: any) {
        // There may be pending upload requests that fail after we cancel, ignore these
        // and only throw if we haven't been canceled.
        if (this.GetUploadState() != OneDriveUploadTaskState.Canceled) {
            let errorString = ErrorStringFromException(error);
            if (typeof error !== "undefined" && typeof error.error !== "undefined" && typeof error.error.code !== "undefined") {
                switch (error.error.code) {
                    case "quotaLimitReached":
                        LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: true, message: "Uploading failed with exception: " + errorString, isFinalResponse: true });
                        throw new OneDriveUploadTaskError(OneDriveUploadTaskErrors.OneDriveQuotaLimitReached);
                    case "notAllowed":
                        // specific handling for 403 error code which comes when proper onedrive access is not there
                        if (error.error.statusCode == 403) {
                          LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: true, message: "Uploading failed with exception: " + errorString, isFinalResponse: true });
                          throw new OneDriveUploadTaskError(OneDriveUploadTaskErrors.ForbiddenOneDriveAccess);
                        }
                        break;
                    case "accessDenied":
                        // specific handling for 403 error code which comes when proper onedrive access is not there
                        if (error.error.statusCode == 403) {
                          LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: true, message: "Uploading failed with exception: " + errorString, isFinalResponse: true });
                          throw new OneDriveUploadTaskError(OneDriveUploadTaskErrors.ForbiddenOneDriveAccess);
                        }
                        break;
                    default:
                        LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Uploading failed with exception: " + errorString, isFinalResponse: true });
                        throw error;
                }
            } else {
                LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Uploading failed with exception: " + errorString, isFinalResponse: true });
                throw error;
            }
        }
    }

    public async Cancel(): Promise<void> {
        LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Canceled", isFinalResponse: true });
        this.uploadTaskState = OneDriveUploadTaskState.Canceled;

        // Cancel upload task if it's been created.
        if (this.graphUploadTask != null) {
            try {
                await this.graphUploadTask.cancel();
                this.graphUploadTask = null;
            } catch (error) {
                // Ignore graph errors during cancellation.
            }
        }

        return;
    }

    private async StartResumableFileUpload() {
        try {
            if (this.graphUploadTask != null) {
                return await this.graphUploadTask!.upload();
            }
            return null;
        } catch (error: any) {

            if (!isNullOrUndefined(this.oneDriveHistory)) {
              this.oneDriveHistory?.AddEventToTranscriptionHistory(" OneDrive upload failed with error " + ErrorStringFromException(error));
            }

            // If NOT TypeError, then throw error right away to execute normal error flow
            this.ThrowIfNotTypeError(error);

            // If TypeError, make 3 attempts to execute it successful
            return AttemptsWithTimeoutCall<Promise<UploadResult>>(
                this.ResumeFileUploadPromise.bind(this),
                RESUME_UPLOAD_RETRY_COUNT,
                RESUME_UPLOAD_WAIT_TIME_MILLISECONDS,
                FRONTDOOR_UPLOAD_WAIT_TIMEOUT_MILLISECONDS,
                this.LogServiceContentForRetry.bind(this),
                () => { },
                this.ThrowIfNotTypeError.bind(this)
            );
        }
    }

    private LogServiceContentForRetry(attemptNumber: number, lastError: any | null) {
        let errorString = "NULL";
        if (!isNullOrUndefined(lastError)) {
            this.ThrowIfNotTypeError(lastError);
            errorString = ErrorStringFromException(lastError);
        }

        LogEvents.ServiceGraphOneDriveUpload.AddServiceContent("TypeError is thrown while uploading file. Will try to resume upload after " +
            (RESUME_UPLOAD_WAIT_TIME_MILLISECONDS / 1000) + " seconds with attempt nunber = " + attemptNumber + ", Exception : " + errorString);
    }

    private ResumeFileUploadPromise(): Promise<UploadResult> {
        return this.graphUploadTask?.resume() as Promise<UploadResult>;
    }

    private ThrowIfNotTypeError(error: any) {
        if (!this.IsTypeError(error)) {
            throw error;
        }
    }

    private IsTypeError(error: any) {
        return typeof error !== "undefined" && error.code == "TypeError";
    }

    public async getOneDriveItemByIdRequest(driveId: any, itemId: any) : Promise<any> {
      try {
        if (this.graphClient == null) {
            this.graphClient = Client.initWithMiddleware({
            baseUrl: GraphClientInitOptions.GetAuthOptions(this.authProvider).baseUrl,
            middleware: GraphClientInitOptions.GetDefaultMiddlewareChain(this.authProvider, true /*enableTelemetryHandler*/, LogEventNames.ServiceGraphClientOneDriveUpload),
            customHosts: GraphClientInitOptions.GetCustomHosts(this.authProvider)
            });
        }
        const itemResponse =  await PromiseWithRetry<any>(() => {
            return GraphGetOneDriveItemByIdRequest(this.graphClient!, driveId, itemId)
          },
          GRAPH_API_ONE_DRIVE_ITEM_FETCH_RETRY_ATTEMPTS,
          GRAPH_API_ONE_DRIVE_ITEM_FETCH_WAIT_TIME
        )

        this.graphClient = null;
        return itemResponse;
      } catch (e) {
        throw new Error('Exception fetching one drive info from graph API'+ ErrorStringFromException(e))
      }
    }
}